<script lang="ts">
import { navigateTo, useRoute } from '#app/composables/router';
import { useSeoMeta } from '#app/composables/head';
import { computed } from 'vue';
import type { RouteLocationNormalized } from 'vue-router';

const AUTHOR_DETAIL_STATE_KEY = 'public-article-recipe-authors-detail';

export async function middleware(to: RouteLocationNormalized) {
  const params = to.params as { id: string };

  // NOTE: async関数内でawait後にcomposableを呼び出すと
  // Nuxtインスタンスのコンテキストが失われるため、事前に取得しておく
  const nuxtApp = useNuxtApp();
  const stateKey = `${AUTHOR_DETAIL_STATE_KEY}-${params.id}`;
  const state = useState<AuthorDetailResponse | null>(stateKey, () => null);
  const { setBreadcrumbs } = useBreadcrumbs();

  try {
    const data = await $fetch<AuthorDetailResponse>(
      '/api/kiwi_user/authors/detail',
      {
        query: {
          author_id: params.id,
        },
      },
    );

    state.value = data ?? null;

    if (data) {
      setBreadcrumbs([
        {
          to: '/public/article/recipe/top',
          text: 'ダイエットレシピ',
        },
        {
          text: `${data.name}のレシピ`,
        },
      ]);
    }
  } catch (e) {
    state.value = null;
    await nuxtApp.runWithContext(() => showError(e as Error));
  }
}
</script>

<script setup lang="ts">
import { useAuthorImageUrl } from '@shared/app/composables/useAuthorImageUrl';
import { useRecipeImageUrl } from '@shared/app/composables/useRecipeImageUrl';
import { normalizeNumberQueryParameter } from '@shared/app/utils/queryNormalizers';
import { definePageMeta } from '#imports';
import { useFetch } from '#app/composables/fetch';
import { useNuxtApp } from '#app/nuxt';
import { showError } from '#app/composables/error';
import { useState } from '#app/composables/state';
import type { AuthorDetailResponse } from '~~/server/api/kiwi_user/authors/detail';
import { useOgImage } from '~/composables/useOgImage';
import { useBreadcrumbs } from '~/composables/useBreadcrumbs';

definePageMeta({
  layout: 'recipe',
  middleware,
});

const route = useRoute('public-article-recipe-authors-id');

const { createAuthorImageUrl } = useAuthorImageUrl();
const { createRecipeMainImageUrl } = useRecipeImageUrl();

const currentPage = computed(() => {
  return normalizeNumberQueryParameter(route.query.page as string, 1);
});

const stateKey = `${AUTHOR_DETAIL_STATE_KEY}-${route.params.id}`;
const authorData = useState<AuthorDetailResponse | null>(stateKey, () => null);

const limit = 18;

const { data: recipesData } = await useFetch('/api/kiwi_user/recipes', {
  query: {
    page: computed(() => currentPage.value),
    author_id: route.params.id,
    limit,
  },
});

const title = computed(() => {
  return `${authorData.value?.name}のレシピ`;
});

const firstRecipe = computed(() => {
  return recipesData.value?.recipes[0];
});

const firstRecipeImage = computed(() => {
  return firstRecipe.value?.main_image
    ? createRecipeMainImageUrl({
        recipeId: firstRecipe.value.id,
        filename: firstRecipe.value.main_image,
        w: 1200,
        h: 630,
      })
    : undefined;
});

const { ogImage } = useOgImage(firstRecipeImage.value);

const description = computed(() => {
  const recipeTitles = recipesData.value?.recipes
    .map((recipe) => recipe.title)
    .join(', ');

  return `${authorData.value?.title} ${authorData.value?.name}の作ったレシピを掲載しています。${recipeTitles}`;
});

useSeoMeta({
  title,
  description,
  ogImage,
});

async function changePage(page: number) {
  await navigateTo({
    query: {
      ...route.query,
      page,
    },
  });
}
</script>

<template>
  <div
    class="relative mb-5 border border-b-2 border-cream-500 md:rounded text-gray-500 bg-white overflow-hidden"
  >
    <div class="absolute top-0 left-0 h-[152px] md:h-[132px] w-full bg-white">
      <img
        v-if="firstRecipe?.main_image"
        class="w-full h-full object-cover"
        :src="
          createRecipeMainImageUrl({
            recipeId: firstRecipe.id,
            filename: firstRecipe.main_image,
            w: 640,
            h: 260,
          })
        "
      />
      <AtomsPlaceholderImage v-else class="w-full h-full object-cover" />
    </div>

    <div class="relative pt-[130px] md:pt-[85px] row-start-1 p-5 z-10">
      <div class="flex items-end mb-2">
        <div
          class="flex-none mr-4 w-[86px] h-[86px] md:w-[114px] md:h-[114px] border border-white rounded overflow-hidden"
        >
          <img
            v-if="authorData?.profile_image"
            :src="
              createAuthorImageUrl({
                authorId: authorData.id,
                filename: authorData.profile_image,
                w: 150,
                h: 150,
              })
            "
          />
          <AtomsPlaceholderImage v-else class="h-full object-cover bg-white" />
        </div>

        <div class="font-bold">
          <p class="mb-2 text-pink-400 line-clamp-1">{{ authorData?.title }}</p>
          <h2 class="text-2xl">{{ title }}</h2>
        </div>
      </div>
      <p v-if="authorData?.description" class="text-lg leading-[1.5]">
        {{ authorData.description }}
      </p>

      <AtomsAuthorUrlList :urls="authorData?.urls ?? []" />
    </div>
  </div>

  <div class="px-3 md:px-0">
    <ul class="grid grid-cols-2 md:grid-cols-3 gap-3 items-stretch mb-3">
      <li
        v-for="recipe in recipesData?.recipes"
        :key="recipe.old_identifier"
        class="h-full"
      >
        <AtomsRecipeCard
          :old-identifier="recipe.old_identifier"
          :recipe-id="recipe.id"
          :filename="recipe.main_image"
          :energy="recipe.energy"
          :title="recipe.title"
          :tags="recipe.tags"
        />
      </li>
    </ul>

    <div
      class="p-5 border border-b-2 border-cream-500 rounded text-center text-gray-500 my-5 bg-white"
    >
      <AtomsRecipePagination
        class="mb-4"
        :total-pages="recipesData?.meta.totalPages ?? 0"
        :current-page="currentPage"
        @change-page="changePage"
      />

      <AtomsRecipePaginationCount
        :total-count="recipesData?.meta.totalCount ?? 0"
        :current-page="currentPage"
        :limit="limit"
      />
    </div>

    <OrganismsRecipeBottomAdsPc />
  </div>
</template>
